import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import XTAMSpinner from '../components/XTAMSpinner';
import DeviceConfigModal from '../components/DeviceConfigModal';
import {
  Search, Camera, CheckCircle, ChevronLeft, ChevronRight,
  RefreshCw, Video, Film, MapPin, Settings, AlertCircle
} from 'lucide-react';
import { inventoryCameraOperational } from '../data/inventoryCameraOperational';
import './CamerasPage.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3009/api/v1';

interface DeviceCameraInfo {
  id: number;
  name: string | null;
  ipAddress: string | null;
  urlRtsp: string | null;
  isOnvif: boolean;
  onvifUrl: string | null;
  onvifUsername: string | null;
  latitude: string | null;
  longitude: string | null;
  xtamVideoChannel: string | null;
  isFromDvr: boolean;
  isAllowTesting: boolean;
  isAllowAnalytics: boolean;
  vmsId: string | null;
  isMassive: boolean;
  urlRtspOriginal: string | null;
  recordingServerUrl: string | null;
  /** FK a devices_sirens: bocina montada en el poste de esta cámara. */
  sirenDeviceId?: number | null;
  siteAddress: string | null;
  siteLatitude: string | null;
  siteLongitude: string | null;
  siteServiceId: number | null;
  /** ``devices_cameras_status.status`` (API: connectivityOnline). */
  connectivityOnline?: boolean | null;
  lastConnectivityStatus?: boolean | null;
  lastHeartbeatAt?: string | null;
  probeState?: number | null;
}

interface InventoryCamera {
  id: number;
  serial: string;
  plate: string;
  mac?: string | null;
  idBrand: number;
  idModel: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  brandName?: string;
  modelName?: string;
  siteId?: number;
  siteName?: string;
  deviceCamera?: DeviceCameraInfo;
}

const CamerasPage: React.FC = () => {
  const { token } = useAuth();
  const navigate = useNavigate();

  const [cameras, setCameras] = useState<InventoryCamera[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [search, setSearch] = useState('');
  const [activeFilter, setActiveFilter] = useState<'all' | 'true' | 'false'>('all');
  const [statusFilter, setStatusFilter] = useState<
    'all' | 'on' | 'off_or_issue' | 'assigned'
  >('all');

  const [currentPage, setCurrentPage] = useState(1);
  const PAGE_SIZE = 20;

  const [editingCamera, setEditingCamera] = useState<InventoryCamera | null>(null);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [siteSirens, setSiteSirens] = useState<Array<{ id: number; ipaddress?: string | null }>>([]);
  const [saving, setSaving] = useState(false);

  const authHeaders = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  useEffect(() => {
    if (!token) return;
    loadCameras();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const loadCameras = async () => {
    setLoading(true);
    setError(null);
    try {
      const params: any = {};
      if (search.trim()) params.search = search.trim();
      if (activeFilter !== 'all') params.isActive = activeFilter;

      const response = await axios.get(`${API_URL}/inventory/cameras`, {
        headers: authHeaders,
        params,
      });

      setCameras(response.data?.data ?? response.data ?? []);
    } catch (e: any) {
      const apiMsg = e.response?.data?.error?.message || e.response?.data?.message || e.message;
      setError(apiMsg || 'Error cargando cámaras');
      setCameras([]);
    } finally {
      setLoading(false);
    }
  };

  const stats = useMemo(() => {
    const total = cameras.length;
    const assigned = cameras.filter(c => c.siteId).length;
    let on = 0;
    let offOrIssue = 0;
    for (const c of cameras) {
      const op = inventoryCameraOperational(c);
      if (op === 'on') on += 1;
      if (op === 'off' || op === 'issue') offOrIssue += 1;
    }
    return { total, on, offOrIssue, assigned };
  }, [cameras]);

  const filteredCameras = useMemo(() => {
    if (statusFilter === 'on') {
      return cameras.filter((c) => inventoryCameraOperational(c) === 'on');
    }
    if (statusFilter === 'off_or_issue') {
      return cameras.filter((c) => {
        const op = inventoryCameraOperational(c);
        return op === 'off' || op === 'issue';
      });
    }
    if (statusFilter === 'assigned') return cameras.filter((c) => c.siteId);
    return cameras;
  }, [cameras, statusFilter]);

  const totalPages = Math.ceil(filteredCameras.length / PAGE_SIZE);
  const paginatedCameras = filteredCameras.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const handleLoadAndReset = () => {
    setCurrentPage(1);
    loadCameras();
  };

  const openStream = (cam: InventoryCamera) => {
    if (!cam.siteId) return;
    const params = new URLSearchParams();
    params.set('siteId', String(cam.siteId));
    if (cam.siteName) params.set('siteName', cam.siteName);
    navigate(`/streams?${params.toString()}`);
  };

  const openRecordings = (cam: InventoryCamera) => {
    if (!cam.siteId) return;
    const params = new URLSearchParams();
    params.set('siteId', String(cam.siteId));
    if (cam.siteName) params.set('siteName', cam.siteName);
    navigate(`/recordings?${params.toString()}`);
  };

  const openEditConfig = async (cam: InventoryCamera) => {
    setEditingCamera(cam);
    setSiteSirens([]);
    setShowConfigModal(true);
    // Cargar las bocinas del sitio para poder marcar cuál cámara tiene la bocina.
    if (cam.siteId) {
      try {
        const res = await axios.get(`${API_URL}/sites/${cam.siteId}/sirens`, { headers: authHeaders });
        setSiteSirens(res.data?.data || []);
      } catch {
        setSiteSirens([]);
      }
    }
  };

  const handleSaveConfig = async (config: any) => {
    if (!editingCamera?.siteId || !editingCamera?.deviceCamera) return;
    setSaving(true);
    try {
      const payload: any = {
        name: config.name,
        siteAddress: config.siteAddress,
        urlRtsp: config.urlRtsp,
        isOnvif: config.isOnvif || false,
        onvifUrl: config.onvifUrl,
        onvifUsername: config.onvifUsername,
        onvifPassword: config.onvifPassword,
        latitude: config.latitude,
        longitude: config.longitude,
        xtamVideoChannel: config.xtamVideoChannel,
        isFromDvr: config.isFromDvr || false,
        isAllowTesting: config.isAllowTesting !== false,
        isAllowAnalytics: config.isAllowAnalytics || false,
        vmsId: config.vmsId,
        isMassive: config.isMassive || false,
        urlRtspOriginal: config.urlRtspOriginal,
        recordingServerUrl: config.recordingServerUrl,
        sirenDeviceId: config.sirenDeviceId ?? null,
      };

      await axios.put(
        `${API_URL}/sites/${editingCamera.siteId}/cameras/${editingCamera.deviceCamera.id}`,
        payload,
        { headers: authHeaders }
      );

      setShowConfigModal(false);
      setEditingCamera(null);
      toast.success('Configuración de cámara guardada correctamente');
      await loadCameras();
    } catch (e: any) {
      const apiMsg = e.response?.data?.error?.message || e.response?.data?.message || e.message;
      toast.error(apiMsg || 'Error guardando configuración');
    } finally {
      setSaving(false);
    }
  };

  const getCameraDisplayName = (cam: InventoryCamera): string => {
    if (cam.deviceCamera?.name) return cam.deviceCamera.name;
    return cam.serial;
  };

  const operationalUi = (cam: InventoryCamera): { mod: string; label: string; title: string } => {
    const op = inventoryCameraOperational(cam);
    const dc = cam.deviceCamera;
    const hb = dc?.lastHeartbeatAt
      ? `Último heartbeat: ${new Date(dc.lastHeartbeatAt).toLocaleString()}`
      : '';
    switch (op) {
      case 'on':
        return {
          mod: 'on',
          label: 'ON',
          title: [hb, 'devices_cameras_status.status en línea'].filter(Boolean).join(' — '),
        };
      case 'off':
        return {
          mod: 'off',
          label: 'OFF',
          title: [hb, 'Sin conectividad (status)'].filter(Boolean).join(' — '),
        };
      case 'issue':
        return {
          mod: 'issue',
          label: 'Con novedad',
          title: [hb, 'Cambio entre last_status y status'].filter(Boolean).join(' — '),
        };
      default:
        return {
          mod: 'unknown',
          label: 'Sin dato',
          title: 'Sin fila en devices_cameras_status o sin state de sonda',
        };
    }
  };

  return (
    <div className="cameras-page">
      {/* Header */}
      <div className="cameras-page-header">
        <div className="cameras-page-header-content">
          <div>
            <h1 className="cameras-page-title">Gestión de Cámaras</h1>
            <p className="cameras-page-subtitle">
              {cameras.length} cámaras registradas
              {stats.assigned > 0 && ` · ${stats.assigned} asignadas a sitios`}
            </p>
          </div>
        </div>

        {/* Dashboard Cards */}
        <div className="cameras-dashboard">
          <button
            type="button"
            onClick={() => { setStatusFilter('all'); setCurrentPage(1); }}
            className={`cameras-stat-card ${statusFilter === 'all' ? 'cameras-stat-active-red' : 'cameras-stat-default-red'}`}
          >
            <div className="cameras-stat-left">
              <span className="cameras-stat-label">Total Cámaras</span>
              <span className="cameras-stat-number">{stats.total}</span>
            </div>
            <Camera size={24} className="cameras-stat-icon cameras-stat-icon-red" />
          </button>
          <button
            type="button"
            onClick={() => { setStatusFilter('on'); setCurrentPage(1); }}
            className={`cameras-stat-card ${statusFilter === 'on' ? 'cameras-stat-active-green' : 'cameras-stat-default'}`}
          >
            <div className="cameras-stat-left">
              <span className="cameras-stat-label">ON</span>
              <span className="cameras-stat-number cameras-stat-green">{stats.on}</span>
            </div>
            <CheckCircle size={24} className="cameras-stat-icon" />
          </button>
          <button
            type="button"
            onClick={() => { setStatusFilter('off_or_issue'); setCurrentPage(1); }}
            className={`cameras-stat-card ${statusFilter === 'off_or_issue' ? 'cameras-stat-active-red' : 'cameras-stat-default'}`}
          >
            <div className="cameras-stat-left">
              <span className="cameras-stat-label">OFF / con novedad</span>
              <span className="cameras-stat-number cameras-stat-red">{stats.offOrIssue}</span>
            </div>
            <AlertCircle size={24} className="cameras-stat-icon cameras-stat-icon-red" />
          </button>
          <button
            type="button"
            onClick={() => { setStatusFilter('assigned'); setCurrentPage(1); }}
            className={`cameras-stat-card ${statusFilter === 'assigned' ? 'cameras-stat-active-blue' : 'cameras-stat-default'}`}
          >
            <div className="cameras-stat-left">
              <span className="cameras-stat-label">Asignadas</span>
              <span className="cameras-stat-number cameras-stat-blue">{stats.assigned}</span>
            </div>
            <MapPin size={24} className="cameras-stat-icon" />
          </button>
        </div>

        {/* Búsqueda y Filtros */}
        <div className="cameras-search-and-filters">
          <div className="cameras-search-container">
            <Search className="cameras-search-icon" size={20} />
            <input
              type="text"
              placeholder="Buscar por serial, placa, MAC, marca, modelo, sitio, IP..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleLoadAndReset(); }}
              className="cameras-search-input"
            />
          </div>

          <select
            className="cameras-filter-select"
            value={activeFilter}
            onChange={(e) => setActiveFilter(e.target.value as any)}
          >
            <option value="all">Todas las cámaras</option>
            <option value="true">Activas</option>
            <option value="false">Inactivas</option>
          </select>

          <button className="cameras-btn-secondary" onClick={handleLoadAndReset}>
            <RefreshCw size={16} />
            Buscar
          </button>
        </div>
      </div>

      {error && <div className="cameras-error">{error}</div>}

      {/* Tabla de cámaras */}
      <div className="cameras-content">
        <div className="cameras-table-container">
          <div className="cameras-table-header">
            <h2>Listado de Cámaras</h2>
          </div>

          {loading ? (
            <XTAMSpinner variant="inline" message="Cargando cámaras..." />
          ) : (
            <table className="cameras-table">
              <thead>
                <tr>
                  <th>Cámara</th>
                  <th>Marca / Modelo</th>
                  <th>Sitio</th>
                  <th>IP</th>
                  <th title="Mismo criterio que Sitios (status + transiciones)">Estado</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filteredCameras.length === 0 && (
                  <tr>
                    <td colSpan={6} className="cameras-empty">
                      <Camera className="cameras-empty-icon" size={48} />
                      <p>No se encontraron cámaras</p>
                    </td>
                  </tr>
                )}

                {paginatedCameras.map((cam) => (
                  <tr key={cam.id}>
                    <td>
                      <div className="cameras-cell-with-icon">
                        <div className="cameras-icon-badge">
                          <Camera size={20} />
                        </div>
                        <div>
                          <div className="cameras-cell-title">{getCameraDisplayName(cam)}</div>
                          <div className="cameras-cell-subtitle">
                            SN: {cam.serial}
                            {cam.plate && ` · ${cam.plate}`}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td>
                      {cam.brandName || cam.modelName ? (
                        <div>
                          <div className="cameras-cell-text">
                            {cam.brandName || '-'}
                          </div>
                          <div className="cameras-cell-subtitle">
                            {cam.modelName || '-'}
                          </div>
                        </div>
                      ) : (
                        <span className="cameras-cell-text">-</span>
                      )}
                    </td>
                    <td>
                      {cam.siteName ? (
                        <span className="cameras-badge cameras-badge-info">
                          <MapPin size={14} />
                          {cam.siteName}
                        </span>
                      ) : (
                        <span className="cameras-badge cameras-badge-inactive">
                          Sin asignar
                        </span>
                      )}
                    </td>
                    <td>
                      <div className="cameras-cell-mono">
                        {cam.deviceCamera?.ipAddress || cam.mac || '-'}
                      </div>
                    </td>
                    <td>
                      {(() => {
                        const { mod, label, title } = operationalUi(cam);
                        return (
                          <span className={`cameras-op-sema cameras-op-sema--${mod}`} title={title}>
                            <span className="cameras-op-sema__dot" aria-hidden />
                            <span>{label}</span>
                          </span>
                        );
                      })()}
                    </td>
                    <td>
                      <div className="cameras-actions">
                        <button
                          className="cameras-action-btn cameras-action-btn-stream"
                          onClick={() => openStream(cam)}
                          disabled={!cam.siteId}
                          title={cam.siteId ? 'Ver stream en vivo' : 'Sin sitio asignado'}
                        >
                          <Video size={16} />
                        </button>
                        <button
                          className="cameras-action-btn cameras-action-btn-recordings"
                          onClick={() => openRecordings(cam)}
                          disabled={!cam.siteId}
                          title={cam.siteId ? 'Ver grabaciones' : 'Sin sitio asignado'}
                        >
                          <Film size={16} />
                        </button>
                        <button
                          className="cameras-action-btn cameras-action-btn-edit"
                          onClick={() => openEditConfig(cam)}
                          disabled={!cam.deviceCamera}
                          title={cam.deviceCamera ? 'Editar configuración' : 'Sin configuración de dispositivo'}
                        >
                          <Settings size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {/* Paginación */}
          {totalPages > 1 && (
            <div className="cameras-pagination">
              <div className="cameras-pagination-info">
                Mostrando {((currentPage - 1) * PAGE_SIZE) + 1} - {Math.min(currentPage * PAGE_SIZE, filteredCameras.length)} de {filteredCameras.length} cámaras
              </div>
              <div className="cameras-pagination-controls">
                <button
                  className="cameras-pagination-btn"
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1}
                  title="Primera página"
                >
                  «
                </button>
                <button
                  className="cameras-pagination-btn"
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                >
                  <ChevronLeft size={16} />
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter(page => {
                    if (totalPages <= 7) return true;
                    if (page === 1 || page === totalPages) return true;
                    if (Math.abs(page - currentPage) <= 1) return true;
                    return false;
                  })
                  .reduce<(number | string)[]>((acc, page, idx, arr) => {
                    if (idx > 0 && typeof arr[idx - 1] === 'number' && (page as number) - (arr[idx - 1] as number) > 1) {
                      acc.push('...');
                    }
                    acc.push(page);
                    return acc;
                  }, [])
                  .map((item, idx) =>
                    item === '...' ? (
                      <span key={`dots-${idx}`} className="cameras-pagination-dots">...</span>
                    ) : (
                      <button
                        key={item}
                        className={`cameras-pagination-btn ${currentPage === item ? 'active' : ''}`}
                        onClick={() => setCurrentPage(item as number)}
                      >
                        {item}
                      </button>
                    )
                  )}
                <button
                  className="cameras-pagination-btn"
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                >
                  <ChevronRight size={16} />
                </button>
                <button
                  className="cameras-pagination-btn"
                  onClick={() => setCurrentPage(totalPages)}
                  disabled={currentPage === totalPages}
                  title="Última página"
                >
                  »
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modal de configuración de cámara */}
      {editingCamera && (
        <DeviceConfigModal
          isOpen={showConfigModal}
          onClose={() => { setShowConfigModal(false); setEditingCamera(null); setSiteSirens([]); }}
          onSave={handleSaveConfig}
          deviceType="camera"
          deviceInfo={{
            id: editingCamera.id,
            serial: editingCamera.serial,
            brand: editingCamera.brandName,
            model: editingCamera.modelName,
          }}
          initialConfig={editingCamera.deviceCamera ? {
            name: editingCamera.deviceCamera.name || '',
            siteAddress: editingCamera.deviceCamera.siteAddress || '',
            urlRtsp: editingCamera.deviceCamera.urlRtsp || '',
            isOnvif: editingCamera.deviceCamera.isOnvif,
            onvifUrl: editingCamera.deviceCamera.onvifUrl || '',
            onvifUsername: editingCamera.deviceCamera.onvifUsername || '',
            latitude: editingCamera.deviceCamera.latitude || '',
            longitude: editingCamera.deviceCamera.longitude || '',
            xtamVideoChannel: editingCamera.deviceCamera.xtamVideoChannel || '',
            isFromDvr: editingCamera.deviceCamera.isFromDvr,
            isAllowTesting: editingCamera.deviceCamera.isAllowTesting,
            isAllowAnalytics: editingCamera.deviceCamera.isAllowAnalytics,
            vmsId: editingCamera.deviceCamera.vmsId || '',
            isMassive: editingCamera.deviceCamera.isMassive,
            urlRtspOriginal: editingCamera.deviceCamera.urlRtspOriginal || '',
            recordingServerUrl: editingCamera.deviceCamera.recordingServerUrl || '',
            sirenDeviceId: editingCamera.deviceCamera.sirenDeviceId ?? null,
          } : undefined}
          siteServiceId={editingCamera.deviceCamera?.siteServiceId ?? undefined}
          siteLatitude={editingCamera.deviceCamera?.siteLatitude ?? undefined}
          siteLongitude={editingCamera.deviceCamera?.siteLongitude ?? undefined}
          siteSirens={siteSirens}
        />
      )}
    </div>
  );
};

export default CamerasPage;
